import type { CSSProperties } from "react"

import {
  getRaceInitial,
  getRaceShieldColors,
  getShieldColors,
} from "@/lib/race-colors"
import { cn } from "@/lib/utils"

// Golden ratio: height = width * 1.618
const SIZE_MAP = {
  xs: { width: 20, height: 32, fontSize: "0.5rem" },
  sm: { width: 32, height: 52, fontSize: "0.625rem" },
  md: { width: 48, height: 78, fontSize: "0.875rem" },
  lg: { width: 64, height: 104, fontSize: "1.125rem" },
} as const

/**
 * Compute a parallel-offset V-polyline.
 * The pennant V is: (0, h*0.7) → (w/2, h) → (w, h*0.7).
 * We offset each leg perpendicular to its direction by `d` pixels outward,
 * then trim by `trim` fraction (0–1) from each end.
 */
function offsetV(w: number, h: number, d: number, trim: number): string {
  // Left leg: (0, h*0.7) → (w/2, h)
  const dx = w / 2
  const dy = h * 0.3
  const len = Math.sqrt(dx * dx + dy * dy)
  // Outward normal (pointing down-left for left leg)
  const nx = -dy / len
  const ny = dx / len

  const lx = nx * d
  const ly = h * 0.7 + ny * d
  const rx = w - nx * d
  const ry = h * 0.7 + ny * d

  const bisectorD = d / Math.cos(Math.atan2(dx, dy))
  const bmx = w / 2
  const bmy = h + bisectorD

  const tlx = lx + (bmx - lx) * trim
  const tly = ly + (bmy - ly) * trim
  const trx = rx + (bmx - rx) * trim
  const trY = ry + (bmy - ry) * trim

  return `${tlx},${tly} ${bmx},${bmy} ${trx},${trY}`
}

interface RaceShieldProps {
  race: string
  /** When provided, overrides race-derived hue. */
  hue?: number | null
  /** When provided, overrides race-derived initials. */
  label?: string
  size?: "xs" | "sm" | "md" | "lg"
  className?: string
}

export function RaceShield({
  race,
  hue,
  label,
  size = "md",
  className,
}: RaceShieldProps) {
  const { width, height, fontSize } = SIZE_MAP[size]
  const { gradientTop, gradientBottom, textColor } =
    hue != null ? getShieldColors(hue) : getRaceShieldColors(race)
  const initial = label ?? getRaceInitial(race)

  const lineDimColor = textColor.replace(")", " / 0.4)")

  const innerPoints = offsetV(width, height, 2, 0.08)
  const outerPoints = offsetV(width, height, 5, 0.18)

  return (
    <div
      className={cn(
        "relative flex items-center justify-center overflow-visible",
        className,
      )}
      style={
        {
          width,
          height,
          "--emblem-bg": `linear-gradient(180deg, ${gradientTop}, ${gradientBottom})`,
        } as CSSProperties
      }
      aria-label={`${race} emblem`}
    >
      <div
        className="absolute inset-0 [clip-path:polygon(0_0,100%_0,100%_70%,50%_100%,0_70%)]"
        style={{ background: "var(--emblem-bg)" }}
      />
      {size !== "xs" && (
        <>
          {/* Outer V-lines (dimmer, shortest) */}
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
            style={{ width, height }}
            viewBox={`0 0 ${width} ${height}`}
          >
            <polyline
              points={outerPoints}
              fill="none"
              stroke={lineDimColor}
              strokeWidth="1"
            />
          </svg>
          {/* Inner V-lines (brighter, shorter than pennant) */}
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
            style={{ width, height }}
            viewBox={`0 0 ${width} ${height}`}
          >
            <polyline
              points={innerPoints}
              fill="none"
              stroke={textColor}
              strokeWidth="1"
            />
          </svg>
        </>
      )}
      <span
        className="font-display relative mb-[30%] leading-none font-bold select-none"
        style={{ fontSize, color: textColor }}
      >
        {initial}
      </span>
    </div>
  )
}
